import { Card, CardContent, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/contexts/AuthContext";
import { trackEvent } from "src/hooks/useInstrumentation";
import { useCurrentUser } from "src/SessionBoundary";
import Button from "src/shared/components/Button";
import FullscreenBox from "src/shared/components/FullScreenBox";
import { ScreenTitle } from "src/shared/components/Typography";
import buildRegistrationUrl from "src/utils/buildRegistrationUrl";
import isInCustody from "src/utils/isInCustody";

function ReviewScreenLayout({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return (
    <FullscreenBox
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
      })}
    >
      <Stack
        spacing={6}
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingX: theme.spacing(2),
          marginTop: "100px",
        })}
      >
        {children}
      </Stack>
    </FullscreenBox>
  );
}

function InCustodyReviewScreen() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { logout } = useAuth();

  const handleLogoutAndNext = async () => {
    const registrationUrl = buildRegistrationUrl({
      cohortId: user.cohortId,
      organizationId: user.organizationId,
    });
    await logout();
    // Use replace to prevent back navigation to the review screen
    window.location.replace(registrationUrl);
  };

  return (
    <ReviewScreenLayout>
      <ScreenTitle sx={{ textAlign: "center" }}>
        {t("We're reviewing your application, {{ firstName }}!", {
          firstName: user.firstName,
        })}
      </ScreenTitle>

      <Card sx={{ width: "100%", maxWidth: 600 }}>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h6" textAlign="center">
              {t("Thank you for your patience")}
            </Typography>

            <Typography>
              {t(
                "Our team is carefully reviewing your application to ensure we can provide you with the best possible support. This process typically takes 7-10 business days."
              )}
            </Typography>

            <Typography>
              {t(
                "The program staff of your facility will notify you with an update soon."
              )}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              onClick={handleLogoutAndNext}
              sx={{ alignSelf: "center" }}
            >
              {t("Log Out & Next Participant")}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </ReviewScreenLayout>
  );
}

function StandardReviewScreen() {
  const { t } = useTranslation();
  const user = useCurrentUser();

  return (
    <ReviewScreenLayout>
      <ScreenTitle sx={{ textAlign: "center" }}>
        {t("We're reviewing your application, {{ firstName }}!", {
          firstName: user.firstName,
        })}
      </ScreenTitle>

      <Card sx={{ width: "100%", maxWidth: 600 }}>
        <CardContent>
          <Stack spacing={3}>
            <Typography variant="h6" textAlign="center">
              {t("Thank you for your patience")}
            </Typography>

            <Typography>
              {t(
                "Our team is carefully reviewing your application to ensure we can provide you with the best possible support. This process typically takes 12-24 hours."
              )}
            </Typography>

            <Typography>
              {t(
                "You'll receive an email notification once your application has been reviewed."
              )}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </ReviewScreenLayout>
  );
}

export default function UnderReviewScreen() {
  const user = useCurrentUser();

  const userIsInCustody = isInCustody(user.incarcerationStatus);

  useEffect(() => {
    trackEvent("Under Review Screen - Viewed", {
      applicationStatus: user.applicationStatus,
      isInCustody: userIsInCustody ? "true" : "false",
    });
  }, [user.applicationStatus, userIsInCustody]);

  if (userIsInCustody) {
    return <InCustodyReviewScreen />;
  }

  return <StandardReviewScreen />;
}
